import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';
import {
  AgentPreferredCitiesUpdateDto,
  AgentProfileDto,
  Country,
  FRENCH_DEPARTMENTS_MAP,
  FrenchDepartment,
  PreferredCityDto,
} from '@workspace/data-types';
import { AuraEditWrapperComponent } from '@workspace/ng-aura';
import { AgentCitiesDisplayComponent } from '../display/agent-cities-display.component';
import { AgentCitiesFormComponent } from '../form/agent-cities-form.component';

@Component({
  selector: 'etxe-agent-cities-edit',
  imports: [AuraEditWrapperComponent, AgentCitiesFormComponent, AgentCitiesDisplayComponent],
  templateUrl: './agent-cities-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentCitiesEditComponent {
  readonly initialProfile = input<AgentProfileDto | null>(null);
  readonly initialPreferredCities = input<PreferredCityDto[] | undefined>(undefined);
  readonly isEditing = model<boolean>(false);
  readonly isAllowedToEdit = input<boolean>(true);

  readonly saved = output<AgentPreferredCitiesUpdateDto>();
  readonly profileSaved = output<AgentProfileDto>();

  readonly currentPreferredCities = computed<PreferredCityDto[]>(() => {
    return this.initialPreferredCities() ?? this.initialProfile()?.intervention?.preferredCities ?? [];
  });

  readonly selectedDepartments = computed<string[]>(() => {
    const zones = this.initialProfile()?.intervention?.zones ?? [];
    const frZone = zones.find((z) => z.country === Country.FRANCE);
    return (frZone?.departments as string[]) || [];
  });

  readonly selectedDepartmentsSummary = computed<string>(() => {
    const depts = this.selectedDepartments();
    if (!depts.length) return 'Aucun département configuré';
    const names = depts.slice(0, 3).map((d) => {
      const info = FRENCH_DEPARTMENTS_MAP[d as FrenchDepartment];
      return info ? `${info.code} (${info.name})` : d;
    });
    const extra = depts.length > 3 ? ` + ${depts.length - 3} autre(s)` : '';
    return names.join(', ') + extra;
  });

  onSave(dto: AgentPreferredCitiesUpdateDto | null): void {
    if (!dto) return;
    this.saved.emit(dto);
    const profile = this.initialProfile();
    if (profile) {
      this.profileSaved.emit({
        ...profile,
        intervention: {
          ...profile.intervention,
          preferredCities: dto.preferredCities,
        },
      });
    }
  }
}
